<template>
  <div class="space-y-8 text-center py-8">
    <div>
      <h2 class="text-2xl font-semibold text-gray-900">
        Analyzing Your Website
      </h2>
      <p class="mt-2 text-gray-600">
        {{ stepMessage }}
      </p>
    </div>

    <!-- Progress bar -->
    <div class="max-w-md mx-auto">
      <div class="h-3 bg-gray-200 rounded-full overflow-hidden">
        <div
          class="h-full bg-primary-600 transition-all duration-500 ease-out"
          :style="{ width: `${progress}%` }" />
      </div>
      <p class="mt-2 text-sm text-gray-500">
        {{ progress }}% complete
      </p>
    </div>

    <!-- Step indicators -->
    <div class="flex justify-center gap-4 md:gap-6">
      <div
        v-for="step in steps"
        :key="step.id"
        class="flex flex-col items-center">
        <div
          class="w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300"
          :class="stepClasses(step.id)">
          <svg v-if="isStepComplete(step.id)" class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
          </svg>
          <svg v-else-if="isStepCurrent(step.id)" class="w-6 h-6 text-white animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <component v-else :is="step.icon" class="w-6 h-6 text-gray-400" />
        </div>
        <span class="mt-2 text-xs text-gray-500 max-w-[80px] text-center">{{ step.label }}</span>
      </div>
    </div>

    <!-- Animation -->
    <div class="flex justify-center">
      <div class="relative w-32 h-32">
        <div class="absolute inset-0 rounded-full border-4 border-gray-200" />
        <div
          class="absolute inset-0 rounded-full border-4 border-primary-500 border-t-transparent animate-spin"
          style="animation-duration: 1.5s" />
        <div class="absolute inset-4 rounded-full bg-primary-50 flex items-center justify-center">
          <svg class="w-12 h-12 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, h } from 'vue'

const props = defineProps({
  currentStep: { type: String, default: null },
  progress: { type: Number, default: 0 }
})

// Step icons as render functions
const GlobeIcon = () => h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9' })
])

const CameraIcon = () => h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z' }),
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M15 13a3 3 0 11-6 0 3 3 0 016 0z' })
])

const CodeIcon = () => h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4' })
])

const SparklesIcon = () => h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z' })
])

const CheckCircleIcon = () => h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' })
])

const steps = [
  { id: 'fetching', label: 'Fetching', icon: GlobeIcon },
  { id: 'screenshotting', label: 'Screenshots', icon: CameraIcon },
  { id: 'extracting', label: 'Extracting', icon: CodeIcon },
  { id: 'analyzing', label: 'AI Analysis', icon: SparklesIcon },
  { id: 'normalizing', label: 'Finalizing', icon: CheckCircleIcon }
]

const stepOrder = steps.map((s) => s.id)

const currentStepIndex = computed(() => stepOrder.indexOf(props.currentStep))

const stepMessage = computed(() => {
  const messages = {
    fetching: 'Fetching your website content...',
    screenshotting: 'Taking screenshots of your site...',
    extracting: 'Extracting business information...',
    analyzing: 'AI is analyzing your design and content...',
    normalizing: 'Preparing your data...'
  }
  return messages[props.currentStep] || 'Processing...'
})

function isStepComplete(stepId) {
  return stepOrder.indexOf(stepId) < currentStepIndex.value
}

function isStepCurrent(stepId) {
  return stepId === props.currentStep
}

function stepClasses(stepId) {
  if (isStepComplete(stepId)) return 'bg-green-500'
  if (isStepCurrent(stepId)) return 'bg-primary-600'
  return 'bg-gray-200'
}
</script>
